import React, {
  useEffect, useMemo, useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ISelectOption } from 'types/IClassifier'
import { format, add } from 'date-fns'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { ActiveBorderTransportMeansType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { hasText } from 'shared/lib/common-util'
import { isEnsType } from 'features/declaration/models/helpers'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist, { createTypeOfIdentificationsByModeOfTransportFilter } from 'features/declaration/codelist/api/useCodelist'
import useCodelistOptions from 'features/declaration/codelist/components/useCodelistOptions'
import { blankActiveBorderTransportMeans } from 'features/declaration/models/blankItems'
import E1103Validator from 'features/declaration/form/validations/E1103'
import ActiveBorderTransportMeansValidator from 'features/declaration/form/validations/ActiveBorderTransportMeansValidator'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import Input from 'components/p5/Input'
import FormFieldArray from 'components/p5/FormFieldArray'
import DotLeafPaths from 'helpers/dot-leaf-paths'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import CountriesOfRouting from './CountriesOfRouting'

const scope = 'activeBorderTransportMeans'

type ActiveBorderTransportMeansItemRelPath = DotLeafPaths<ActiveBorderTransportMeansType>
type ActiveBorderTransportMeansItemBasePath = `activeBorderTransportMeans.${number}`
type ActiveBorderTransportMeansItemFieldPath = `${ActiveBorderTransportMeansItemBasePath}.${ActiveBorderTransportMeansItemRelPath}`

function ActiveBorderTransportMeans() {
  const [minDate, setMinDate] = useState(() => format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'))

  const { t } = useTranslation()
  const {
    control,
    getValues,
    setValue,
  } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
    fields,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, ActiveBorderTransportMeansType>({
    control,
    name: scope,
  })

  const borderModeOfTransport = getValues('borderModeOfTransport')
  const transitOffices = getValues('transitCustomsOffices')
  const declarationType = getValues('declarationType')
  const security = getValues('security')
  const status = getValues('status')
  const isEnsMode = useMemo(() => isEnsType(declarationType), [declarationType])

  const activeBorderTransportMeansMultiplicity = ActiveBorderTransportMeansValidator.getMultiplicity(transitOffices, isEnsMode)

  const [, countriesOptions] = useCodelist('COUNTRIES')
  const [, transportMeansTypeOptions] = useCodelist('TRANSPORT_MEANS_TYPES')
  const transportIdentificationsFilterPredicate = useMemo(
    () => createTypeOfIdentificationsByModeOfTransportFilter(borderModeOfTransport),
    [borderModeOfTransport],
  )
  const {
    options: transportIdentificationsOptions,
    isSuccess: isTransportIdentificationsLoaded,
  } = useCodelistOptions('TRANSPORT_IDENTIFICATIONS', transportIdentificationsFilterPredicate)
  const [, customsOfficeAtBorderOptions] = useCodelist('CUSTOMS_OFFICES')

  const itemFieldAt = (
    index: number,
    field: ActiveBorderTransportMeansItemRelPath,
  ): ActiveBorderTransportMeansItemFieldPath => `activeBorderTransportMeans.${index}.${field}` as const

  useEffect(() => {
    if (!isTransportIdentificationsLoaded) {
      return
    }

    getValues(scope)?.forEach((item, index) => {
      const hasCorrectSubType = transportIdentificationsOptions.find((option) => option.value === item.typeOfIdentification)
      if (!borderModeOfTransport || !hasCorrectSubType) {
        setValue(itemFieldAt(index, 'typeOfIdentification'), '', {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    })
  }, [borderModeOfTransport, getValues, isTransportIdentificationsLoaded, setValue, transportIdentificationsOptions])

  useEffect(() => {
    const id = setInterval(() => {
      setMinDate(format(new Date(), 'yyyy-MM-dd\'T\'HH:mm'))
    }, 30_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (['1', '3'].includes(security) && status === 'DRAFT') {
      for (let i = 0; i < fields.length; i++) {
        const activeMeans = fields[i]
        if (!hasText(activeMeans.estimatedDateAndTimeOfDeparture) && !hasText(activeMeans.estimatedDateAndTimeOfArrival)) {
          setValue(itemFieldAt(i, 'estimatedDateAndTimeOfDeparture'), format(add(new Date(), { hours: 3 }), 'yyyy-MM-dd\'T\'HH:mm'))
        }
        if (!hasText(activeMeans.estimatedDateAndTimeOfArrival)) {
          setValue(itemFieldAt(i, 'estimatedDateAndTimeOfArrival'), format(add(new Date(), { days: 5 }), 'yyyy-MM-dd\'T\'HH:mm'))
        }
      }
    }
  }, [security, status, fields, setValue])

  const paths = useMemo(
    () => [
      scope,
      ...visibleFields.map((f) => `${scope}.${f.index}.customsOfficeAtBorderReferenceNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
      ...visibleFields.map((f) => `${scope}.${f.index}.identificationNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.conveyanceReferenceNumber`),
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfMeansOfTransport`),
      ...visibleFields.map((f) => `${scope}.${f.index}.typeOfIdentification`),
      ...visibleFields.map((f) => `${scope}.${f.index}.nationality`),
      ...visibleFields.map((f) => `${scope}.${f.index}.actualDateAndTimeOfDeparture`),
      ...visibleFields.map((f) => `${scope}.${f.index}.estimatedDateAndTimeOfDeparture`),
      ...visibleFields.map((f) => `${scope}.${f.index}.estimatedDateAndTimeOfArrival`),
      ...visibleFields.map((f) => `${scope}.${f.index}.countriesOfRouting`),
    ],
    [visibleFields],
  )

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)
  const isAddForbidden = fieldRequirements.activeBorderTransportMeans?.requirement === 'forbidden'
  const isAddButtonDisabled = isAddForbidden || visibleFields.length >= activeBorderTransportMeansMultiplicity

  function excludeNotAllowedForActiveTransportMeans(option: ISelectOption) {
    return !option.value.endsWith('_20') && !option.value.endsWith('_31') // Excludes codes not allowed by CL219
  }

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-deprture-transport"
        headingLabel={t('declaration.p5.borderTransportMeans')}
        fieldRule={fieldRequirements.activeBorderTransportMeans}
      >
        {visibleFields.map((fieldWithIndex) => {
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const
          const borderTransportMeans = fieldWithIndex.field

          return (
            <FormFieldArray
              key={`borderTransportMeans_${borderTransportMeans.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={borderTransportMeans.sequenceNumber + 1}
              hidden={borderTransportMeans.deleted}
            >
              <FormField
                labelKey="declaration.p5.customsOfficeAtTheBorder"
                fieldRule={fieldRequirements[`${innerItemScope}.customsOfficeAtBorderReferenceNumber`]}
              >
                <FormSelect
                  field={`${innerItemScope}.customsOfficeAtBorderReferenceNumber`}
                  labelKey="declaration.p5.customsOfficeAtTheBorder"
                  options={customsOfficeAtBorderOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.customsOfficeAtBorderReferenceNumber`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.typeOfIdentification"
                fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
              >
                <FormSelect
                  field={`${innerItemScope}.typeOfIdentification`}
                  labelKey="declaration.p5.typeOfIdentification"
                  options={transportIdentificationsOptions.filter(excludeNotAllowedForActiveTransportMeans)}
                  isDisabled={!borderModeOfTransport}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfIdentification`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.identificationNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
              >
                <Input
                  field={`${innerItemScope}.identificationNumber`}
                  labelKey="declaration.p5.identificationNumber"
                  type="text"
                  maxLength={E1103Validator.getIdentificationNumberMaxLength()}
                  placeholder={t('declaration.p5.identificationNumber')}
                  fieldRule={fieldRequirements[`${innerItemScope}.identificationNumber`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.nationality"
                fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
              >
                <FormSelect
                  field={`${innerItemScope}.nationality`}
                  labelKey="declaration.p5.nationality"
                  options={countriesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.nationality`]}
                />
              </FormField>

              <FormField
                labelKey="declaration.p5.conveyanceReferenceNumber"
                fieldRule={fieldRequirements[`${innerItemScope}.conveyanceReferenceNumber`]}
              >
                <Input
                  field={`${innerItemScope}.conveyanceReferenceNumber`}
                  labelKey="declaration.p5.conveyanceReferenceNumber"
                  type="text"
                  placeholder={t('declaration.p5.conveyanceReferenceNumber')}
                  fieldRule={fieldRequirements[`${innerItemScope}.conveyanceReferenceNumber`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.typeOfMeansOfTransport"
                fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
              >
                <FormSelect
                  field={`${innerItemScope}.typeOfMeansOfTransport`}
                  labelKey="declaration.p5.typeOfMeansOfTransport"
                  options={transportMeansTypeOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.typeOfMeansOfTransport`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.actualDateAndTimeOfDeparture"
                fieldRule={fieldRequirements[`${innerItemScope}.actualDateAndTimeOfDeparture`]}
              >
                <Input
                  type="datetime-local"
                  step={1}
                  field={`${innerItemScope}.actualDateAndTimeOfDeparture`}
                  labelKey="declaration.p5.actualDateAndTimeOfDeparture"
                  fieldRule={fieldRequirements[`${innerItemScope}.actualDateAndTimeOfDeparture`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.estimatedDateAndTimeOfDeparture"
                fieldRule={fieldRequirements[`${innerItemScope}.estimatedDateAndTimeOfDeparture`]}
              >
                <Input
                  type="datetime-local"
                  step={1}
                  field={`${innerItemScope}.estimatedDateAndTimeOfDeparture`}
                  labelKey="declaration.p5.estimatedDateAndTimeOfDeparture"
                  min={minDate}
                  fieldRule={fieldRequirements[`${innerItemScope}.estimatedDateAndTimeOfDeparture`]}
                />
              </FormField>
              <FormField
                labelKey="declaration.p5.estimatedDateAndTimeOfArrival"
                fieldRule={fieldRequirements[`${innerItemScope}.estimatedDateAndTimeOfArrival`]}
              >
                <Input
                  type="datetime-local"
                  step={1}
                  field={`${innerItemScope}.estimatedDateAndTimeOfArrival`}
                  labelKey="declaration.p5.estimatedDateAndTimeOfArrival"
                  fieldRule={fieldRequirements[`${innerItemScope}.estimatedDateAndTimeOfArrival`]}
                />
              </FormField>
              {isEnsMode && (
                <CountriesOfRouting activeBorderTransportMeansIndex={fieldWithIndex.index} />
              )}
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddButtonDisabled ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankActiveBorderTransportMeans)}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewTransportMeans')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default ActiveBorderTransportMeans
